import { gql } from '@apollo/client';
import { teamIcon } from '@justgiving/icons';
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  PageStatus,
  PageType,
  TeamDetailsFragment,
} from '../../../types/__generated__';
import config from '../../client/config';
import usePageQuery, {
  buildQuery,
  UsePageQueryResult,
} from '../../hooks/usePageQuery';
import { getFirstTeam } from '../../utils/relationships';
import IconLabel from '../IconLabel/IconLabel';
import IsPageOwner from '../IsPageOwner/IsPageOwner';
import PATHS from '../../constants/paths';

// Beware not all fields are available for old campaigns
// requesting them in 'parent' field
// results in page data being null for requested campaign

const FRAGMENT = buildQuery(gql`
  fragment TeamDetails on Page {
    id
    legacyId
    targetWithCurrency {
      currencyCode
    }
    type
    relationships {
      parents {
        type
        page {
          id
          legacyId
          title
          url
          status
          product {
            name
          }
          type
        }
      }
    }
  }
`);

const TeamDetails: React.FunctionComponent = () => {
  const teamDetails = usePageQuery<TeamDetailsFragment>(FRAGMENT);
  const { t } = useTranslation('common', { useSuspense: false });

  const team = getFirstTeam(teamDetails.data?.relationships?.parents ?? []);
  const acceptedCurrencies = ['AUD', 'CAD', 'EUR', 'GBP', 'NZD', 'USD'];
  const isTargetCurrencyAccepted = acceptedCurrencies.includes(
    teamDetails.data?.targetWithCurrency?.currencyCode,
  );

  if (isTargetCurrencyAccepted) {
    if (!UsePageQueryResult.hasData(teamDetails) || !team) {
      const isOnePage = teamDetails.data?.type === PageType.OnePage;
      const idParamName = isOnePage ? 'pageId' : 'frpid';

      const createTeamUrl = config.flags.useOnePageTeamsCreation
        ? `${config.baseUri}${PATHS.ES_JOIN_URL}/team?fundraisingPageId=${teamDetails.data?.legacyId}`
        : `${config.baseUri}/teams-creation?${idParamName}=${teamDetails.data?.legacyId}`;

      if (isOnePage && !config.flags.showTeamCreationCTAForOnePage) {
        return null;
      }

      return (
        <IsPageOwner>
          <IconLabel
            icon={teamIcon}
            label={<a href={createTeamUrl}>{t('page.createTeamCta')}</a>}
          />
        </IsPageOwner>
      );
    }

    if (team.status === PageStatus.Inactive) {
      return (
        <IsPageOwner>
          <IconLabel
            icon={teamIcon}
            label={
              <>
                <a
                  href={`${config.baseUri}/teams-creation/create/${team.legacyId}/team-name`}
                >
                  {t('page.finishTeamCreation')}
                </a>
                <span>: {team.title}</span>
              </>
            }
          />
        </IsPageOwner>
      );
    }
  }

  if (team) {
    return (
      <IconLabel
        icon={teamIcon}
        label={
          <span>
            Team: <a href={team.url}>{team.title}</a>
          </span>
        }
      />
    );
  }

  return null;
};

export default TeamDetails;
